import { AbortControllerState, AI_RETRY_CONFIG, RetryState } from '../constants/ai/retryConfig';
import { logger } from './logger';
import { isAbortError } from './errorGuards';

/**
 * Determines if an error is eligible for retry
 * @param error - The error to check
 * @returns Whether the error is retryable
 */
export function isRetryableError(error: any): boolean {
  if (!navigator.onLine) {
    return true; // Always retry if we're offline - we might come back online
  }

  if (!(error instanceof Error)) {
    return false; // Non-Error objects are not retryable
  }

  // Network errors
  if (
    error.message.includes('network') ||
    error.message.includes('failed to fetch') ||
    error.message.includes('connection')
  ) {
    return true;
  }

  // Timeout errors
  if (error.message.includes('timeout') || error.message.includes('timed out')) {
    return true;
  }

  // Server errors (5xx)
  if (error.name === 'OpenAIApiError' && 'status' in error && (error as any).status >= 500) {
    return true;
  }

  // Rate limit errors
  if (
    error.name === 'OpenAIApiError' &&
    (('status' in error && (error as any).status === 429) || error.message.includes('rate limit'))
  ) {
    return true;
  }

  return false;
}

/**
 * Get appropriate error message based on error type
 * @param error - The error to get a message for
 * @returns User-friendly error message
 */
export function getAIErrorMessage(error: any): string {
  // Default error message
  let errorMessage =
    'I apologize, but I encountered an error while processing your request. Please try again.';

  // Network connectivity issues
  if (!navigator.onLine) {
    return 'I apologize, but I cannot generate a response while you are offline. Please check your internet connection and try again.';
  }

  if (!(error instanceof Error)) {
    return errorMessage;
  }

  // OpenAI specific errors
  if (error.name === 'OpenAIApiError') {
    errorMessage =
      'I apologize, but there was an issue with the AI service. Please try again in a moment.';

    // Rate limit errors
    if (error.message.includes('rate limit') || error.message.includes('quota')) {
      errorMessage =
        "I apologize, but we've reached the rate limit with our AI provider. Please try again in a few minutes.";
    }
    // Timeout errors
    else if (error.message.includes('timeout') || error.message.includes('timed out')) {
      errorMessage =
        'I apologize, but the request timed out. Please try again with a shorter message.';
    }
  }

  // Generic network errors
  if (error.message.includes('network') || error.message.includes('failed to fetch')) {
    errorMessage =
      'I apologize, but there was a network issue. Please check your connection and try again.';
  }

  return errorMessage;
}

/**
 * Creates an abort controller and provides helpers to manage it
 * @returns AbortController state and helpers
 */
export function createAbortControllerState(): AbortControllerState {
  let controller: AbortController | null = null;

  const setController = (newController: AbortController | null) => {
    controller = newController;
  };

  const getNewController = () => {
    // Abort any existing controller first
    if (controller) {
      controller.abort();
    }

    // Create a new controller
    const newController = new AbortController();
    controller = newController;
    return newController;
  };

  const abortCurrentController = () => {
    if (controller) {
      controller.abort();
      controller = null;
    }
  };

  return {
    controller,
    setController,
    getNewController,
    abortCurrentController,
  };
}

/**
 * Utility to perform an operation with retries
 * @param operation - Async function to execute with retry support
 * @param options - Retry configuration options
 * @returns Result of the operation if successful
 */
export async function executeWithRetry<T>(
  operation: (signal: AbortSignal, retryCount: number) => Promise<T>,
  {
    retryState,
    abortControllerState,
    onRetry,
    onSuccess,
    onError,
  }: {
    retryState: RetryState;
    abortControllerState: AbortControllerState;
    onRetry?: (retryCount: number, error: any) => void;
    onSuccess?: (result: T) => void;
    onError?: (error: any, retryAttempts: number) => void;
  }
): Promise<T> {
  // Reset retry state at the beginning
  retryState.isRetrying = true;
  retryState.retryAttempt = 0;
  retryState.isSilentlyRetrying = false;

  // Get a fresh abort controller
  const controller = abortControllerState.getNewController();
  let currentRetryCount = 0;

  try {
    // Attempt the operation
    const result = await operation(controller.signal, 0);
    onSuccess?.(result);
    return result;
  } catch (error) {
    // If aborted, we don't need to do anything else
    if (isAbortError(error)) {
      logger.ai('Operation was aborted, skipping further processing');
      throw error;
    }

    logger.error('Error in operation:', error);

    // Try to retry if the error is retryable
    while (
      currentRetryCount < AI_RETRY_CONFIG.MAX_RETRIES &&
      navigator.onLine &&
      isRetryableError(error) &&
      retryState.isMounted
    ) {
      // Increment the retry counter
      currentRetryCount++;
      retryState.retryAttempt = currentRetryCount;
      retryState.isSilentlyRetrying = true;

      // Notify of retry
      logger.component('Attempting to retry operation', {
        retryCount: currentRetryCount,
        maxRetries: AI_RETRY_CONFIG.MAX_RETRIES,
        errorType: error instanceof Error ? error.name : 'UnknownError',
      });

      onRetry?.(currentRetryCount, error);

      // Wait before retrying
      const delayMs = AI_RETRY_CONFIG.getRetryDelay(currentRetryCount);
      await new Promise(resolve => setTimeout(resolve, delayMs));

      // Exit early if we've been unmounted or aborted during wait
      if (!retryState.isMounted || controller.signal.aborted) {
        logger.ai('Operation cancelled during retry delay');
        throw new Error('Operation cancelled during retry');
      }

      // Retry the operation
      try {
        const result = await operation(controller.signal, currentRetryCount);
        onSuccess?.(result);
        return result;
      } catch (retryError) {
        // Check if aborted during retry
        if (isAbortError(retryError)) {
          logger.ai('Retry was aborted');
          throw retryError;
        }

        // Log retry error
        logger.error(`Retry ${currentRetryCount} failed:`, retryError);

        // Continue to next retry or final error
      }
    }

    // If we get here, we've exhausted all retries or the error wasn't retryable
    onError?.(error, currentRetryCount);
    throw error;
  } finally {
    // Reset state regardless of outcome
    retryState.isRetrying = false;
    retryState.isSilentlyRetrying = false;
  }
}
